import { notIdentity } from '@op/util'
import { createSelector } from '@reduxjs/toolkit'
import type { BlockStatusMap } from '../reducers/block-status.slice.js'
import { activeChildId$, blockStatusMap$ } from '../reducers/index.js'
import type {
	BlockStatus,
	DeviceBlockStatus,
	IntendedBlockState,
	ViewBlockState,
} from '../types/index.js'

const userIdParamSelector = (_: unknown, userId: string) => userId

const selectBlockStatus = (
	blockStatusMap: BlockStatusMap,
	userId: Maybe<string>,
): Maybe<BlockStatus> => blockStatusMap[userId || '']

export const activeUserBlockStatus$ = /*@__PURE__*/ createSelector(
	blockStatusMap$,
	activeChildId$,
	selectBlockStatus,
)

export const userBlockStatus$ = /*@__PURE__*/ createSelector(
	blockStatusMap$,
	userIdParamSelector,
	selectBlockStatus,
)

const selectIntendedBlockState = (
	blockStatus: Maybe<BlockStatus | DeviceBlockStatus>,
): Maybe<IntendedBlockState> => blockStatus?.intendedState || 'allowed'

export const activeUserIntendedBlockState$ = /*@__PURE__*/ createSelector(
	activeUserBlockStatus$,
	selectIntendedBlockState,
)

export const userIntendedBlockState$ = /*@__PURE__*/ createSelector(
	userBlockStatus$,
	selectIntendedBlockState,
)

const selectHasDeviceBroadcasting = (blockStatus: Maybe<BlockStatus>) =>
	Boolean(blockStatus?.devices.some((d) => d.vewStatus == 'active'))

export const activeUserHasDeviceBroadcasting$ = /*@__PURE__*/ createSelector(
	activeUserBlockStatus$,
	selectHasDeviceBroadcasting,
)

// export const userHasDeviceBroadcasting$ = /*@__PURE__*/ createSelector(
// 	userBlockStatus$,
// 	selectHasDeviceBroadcasting,
// )

/**
 * State defaults to allowed when we don't have data, although, this should be
 * behind a loading state anyway.
 * TODO: Review this fallback. Missing block status data should ideally be
 * represented explicitly instead of defaulting to an access state.
 */
export const activeUserViewBlockState$ = /*@__PURE__*/ createSelector(
	activeUserBlockStatus$,
	(s): ViewBlockState => s?.viewState || 'allowed',
)

export const isActiveUserUnmanaged$ = /*@__PURE__*/ createSelector(
	activeUserViewBlockState$,
	(s) => s == 'not_paired',
)

export const isActiveUserManaged$ = /*@__PURE__*/ createSelector(
	isActiveUserUnmanaged$,
	notIdentity,
)

export const isActiveUserBlockedByAllowance$ = /*@__PURE__*/ createSelector(
	activeUserBlockStatus$,
	(status) => status?.state == 'blocked' && status?.rule.type == 'allowance',
)

export const isActiveUserBlockedByVew$ = /*@__PURE__*/ createSelector(
	activeUserBlockStatus$,
	(status) =>
		status?.state == 'allowed' &&
		status?.devices.some((d) => d.viewState == 'allowed' && d.vewStatus == 'inactive'),
)

export const userDeviceBlockStatus$ = /*@__PURE__*/ createSelector(
	blockStatusMap$,
	(_: unknown, userId: string) => userId,
	(_: unknown, _userId: string, deviceId: string) => deviceId,
	(blockStatusMap, userId, deviceId): Maybe<DeviceBlockStatus> =>
		blockStatusMap[userId]?.devices.find((d) => d.device_id === deviceId),
)

export const userDeviceIntendedBlockState$ = /*@__PURE__*/ createSelector(
	userDeviceBlockStatus$,
	selectIntendedBlockState,
)
